import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos ---------------------- */
export type EstoqueProduto = {
  posicao: string | null;
  codigo: string;
  ncm: string | null;
  nomeProduto: string;
  custoUnitario: string | null; // NUMERIC do PG vem como string
  quantidade: number | null;
  classificacao: string | null;
  moeda: string | null; // <<< NOVO
};

// payload do endpoint /estoque
export type EstoqueRowAPI = {
  codigo: string;
  nome: string;
  classificacao: string | null;
  posicao: string | null;
  quantidade_atual: number | null;
  custo_unitario_atual: string | null; // string pelo driver pg
  moeda?: string | null; // <<< NOVO (vem de ea.moeda)
  ncm?: string | null; // opcional, caso seu SELECT inclua p.ncm
};

type ListaResp = {
  fonte?: "cache" | "banco";
  versao?: number | null;
  data: EstoqueRowAPI[];
};

type AwaitResult = { changed: boolean; versao: number | null };

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/suprimentos";
const URL = `${BASE}/estoque`;
const URL_AWAIT = `${BASE}/estoque/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function sameArray(a: EstoqueProduto[], b: EstoqueProduto[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function normalizeRow(r: EstoqueRowAPI): EstoqueProduto {
  return {
    posicao: r.posicao ?? null,
    codigo: r.codigo,
    ncm: (r as any).ncm ?? null,
    nomeProduto: r.nome,
    custoUnitario: r.custo_unitario_atual ?? null,
    quantidade: r.quantidade_atual ?? null,
    classificacao: r.classificacao ?? null,
    moeda:
      typeof r.moeda === "string" && r.moeda.trim() !== "" ? r.moeda : null,
  };
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** ---------------------- HTTP helpers ---------------------- */
async function headVersao(signal?: AbortSignal): Promise<number | null> {
  try {
    const token = getAccessToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(URL, {
      method: "HEAD",
      headers,
      signal,
    });
    if (!res.ok) return null;
    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;
    return Number.isFinite(n!) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  _versaoAtual: number | null,
  _signal?: AbortSignal
): Promise<{
  fonte: "cache" | "banco" | undefined;
  versao: number | null;
  data: EstoqueProduto[];
}> {
  const token = getAccessToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(URL, {
    method: "GET",
    headers,
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;
  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const json = (await res.json()) as ListaResp;
  const verBody = typeof json?.versao === "number" ? json.versao : null;
  const versao = Number.isFinite(verHdr!) ? verHdr : verBody;

  const data: EstoqueProduto[] = (json?.data ?? []).map(normalizeRow);

  return { fonte, versao: versao ?? null, data };
}

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${since}&timeout_ms=${AWAIT_TIMEOUT_MS}`;

    const token = getAccessToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(url, {
      method: "GET",
      headers,
      signal,
    });
    if (!res.ok) return null;

    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);

    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useEstoqueDisponivel() {
  const [itens, setItens] = useState<EstoqueProduto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, _setVersao] = useState<number | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);

  const fetchingRef = useRef(false);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  const setVersao = (v: number | null) => {
    versaoRef.current = v;
    _setVersao(v);
  };

  async function carregarLista(cur: number | null) {
    fetchingRef.current = true;
    try {
      const lista = await getLista(cur /* sem signal */);
      if (lista.versao != null) setVersao(lista.versao);
      setFonte(lista.fonte);
      setItens((prev) => (sameArray(prev, lista.data) ? prev : lista.data));
    } finally {
      fetchingRef.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    (async () => {
      try {
        const vHead = await headVersao();
        await carregarLista(vHead ?? null);
        setLoading(false);

        while (!stop.current) {
          while (fetchingRef.current && !stop.current) {
            await sleep(25);
          }
          if (stop.current) break;

          const cur = versaoRef.current;

          // ---------- Long-poll preferencial ----------
          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const acAwait = new AbortController();
            awaitCtrlRef.current = acAwait;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, acAwait.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(ERROR_BACKOFF_MS);
              }
            } finally {
              if (awaitCtrlRef.current === acAwait) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            const avancou = (resp?.versao ?? -1) > cur;
            if (resp?.changed || avancou) {
              await carregarLista(cur);
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          // --------- Fallback por HEAD/ETag ---------
          const vNow = await headVersao();
          const cur2 = versaoRef.current;

          if (vNow !== null && cur2 !== null && vNow === cur2) {
            await sleep(POLL_FALLBACK_MS);
            continue;
          }
          if (vNow === null && cur2 !== null) {
            await sleep(POLL_FALLBACK_MS);
          }

          await carregarLista(cur2);
        }
      } catch (e) {
        if (!stop.current) console.error("useEstoqueDisponivel loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetchingRef.current) await sleep(25);
    await carregarLista(versaoRef.current);
  };

  return { itens, loading, refresh, fonte, versao };
}

/** ---------------------- Fetch pontual (sem hook) ---------------------- */
export async function fetchEstoqueOnce(): Promise<{
  itens: EstoqueProduto[];
  fonte: "cache" | "banco" | undefined;
  versao: number | null;
}> {
  const vHead = await headVersao();
  const lista = await getLista(vHead);
  return {
    itens: lista.data,
    fonte: lista.fonte,
    versao: lista.versao ?? null,
  };
}
